import { Injectable } from '@angular/core';
import { ExportContext, ExportFileFormat, ExportRange, ExportService, ExportTaskProcessor } from 'app/layout/export-overlay';
import { OrderAttributes, OrderWarehouseAttributes } from '@albatrosdeveloper/ave-models-package';
import { takeUntil } from 'rxjs';
import get from 'lodash-es/get';
import ceil from 'lodash-es/ceil';
import { OrderCustomerService } from '../../../reports/historic/order-customer/order-customer.service';
import { LOCALIZED_PROPERTIES, ORDER_EXPORT_ID, ORDER_TITLE } from './order.constants';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { DateTime } from 'luxon';
import { formatLuxonDate } from '../../order-list-components/exports/sales-orders-list.utils';
import { OrdersListService } from '../../order-list-components/order-list.service';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';

@Injectable()
export class OrderDeliveryCustomerExport {
  constructor(
    private exportService: ExportService,
    private orderCustomer: OrderCustomerService,
    private ordersListService: OrdersListService,
    private handlerMessage: HandlerMessageService,
  ) {}

  private transformItem(item: OrderAttributes, detail: any) {
    const warehouse = this.getFirstWarehouse(item); // Obtiene el primer almacén

    return {
      serial: get(item, 'serial', ''),
      salesOffice: get(item, 'salesOffice.name', ''),
      route: get(item, 'subRoute.name', ''),
      shipTo: get(item, 'shipTo.externalKeys.code', ''),
      address: get(item, 'address.address', ''),
      paymentCondition: get(item, 'paymentCondition.externalKeys.code', ''),
      expeditionCondition: get(item, 'expeditionCondition.code', ''),
      materialCode: get(detail, 'material.code', ''),
      materialDescription: get(detail, 'material.name', ''),
      warehouse: warehouse?.code || '',
      warehouseName: warehouse?.name || '',
      soldUnitOfMeasure: get(detail, 'soldUnitOfMeasure.code', ''),
      soldQuantity: get(detail, 'soldQuantity', ''),
      confirmedErp: '', //CNT CONFIRMADA
      unitPrice: ExportTaskProcessor.types.number(get(detail, 'unitPrice', '??')),
      note: get(item, 'note', ''),
      deliveryNumber: '',
      invoiceNumber: '',
      deliveryDate: '',
      batchNumber: get(detail, 'batches[0].bacth', ''),
      batchQuantity: get(detail, 'batches[0].quantity', ''),
      createDateTimeDate: formatLuxonDate(get(item, 'createDateTime') || get(item, 'creationDateTime') || get(item, 'createdAt'), 'dd-MM-yyyy'),
      createDateTimeHour: formatLuxonDate(get(item, 'createDateTime') || get(item, 'creationDateTime') || get(item, 'createdAt'), 'hh:mm:ss a'),
      updateAuditAtDate: formatLuxonDate(get(item, 'updateAuditAt') || get(item, 'updatedAt') || get(item, 'updatedAuditAt'), 'dd-MM-yyyy'),
      updateAuditAtHour: formatLuxonDate(get(item, 'updateAuditAt') || get(item, 'updatedAt') || get(item, 'updatedAuditAt'), 'hh:mm:ss a'),
      updatedByUser: (() => { const u=get(item,'updatedBy'); if(!u) return ''; const ul=get(u,'userLogin'); if(ul){const f=get(ul,'first_name',''),l=get(ul,'last_name',''); if(f||l) return `${f} ${l}`.trim(); return get(ul,'username','');} return get(u,'externalSystem.name','Sistema'); })(),
    };
  }


  private getFirstWarehouse(item: OrderAttributes): { code: string, name: string } | null {
    const warehouses = get(item, 'warehouses', []);
    if (!Array.isArray(warehouses) || warehouses.length === 0) return null;
    
    return {
      code: get(warehouses[0], 'externalKeys.code', ''),
      name: get(warehouses[0], 'name', '')
    };
  }
  
  private capitalizeFirstLetter(text: string): string {
    if (!text) return '';
    return text[0].toUpperCase() + text.substring(1).toLowerCase();
  }
  
  export(format: ExportFileFormat, range: ExportRange, totalItems: number, pageSize: number, pageIndex: number, filterData: any) {
    const totalPages = ceil(totalItems / pageSize) + 1;
    this.exportService.task({
      id: ORDER_EXPORT_ID,
      title: this.capitalizeFirstLetter(ORDER_TITLE),
      format: format,
      iterations: range === ExportRange.AllPages ? totalPages : 1,
      start: (ctx: ExportContext) => {
        ctx.processor.localizeItemProperties(ORDER_TITLE, LOCALIZED_PROPERTIES);
      },
      next: (ctx: ExportContext) => {
        const currentPage = range === ExportRange.AllPages ? ctx.iteration : pageIndex;
        let options = { 
          currentPage: currentPage,
          limitPerPage: pageSize,
        }
        
        const send = {
          type: filterData.type,
          creationDateTime: filterData.creationDateTime,
          office: filterData.office,
          route: filterData.route
        }

        this.ordersListService.findAllDownloadWithCount(
          options,
          send
        ).pipe(takeUntil(ctx.stopped)).subscribe({
          next: (response) => {
            try {
              get(response, 'data.data', []).map((item) => { //Este solo descarga en excel
                const details = get(item, 'detail', []);

                if (details.length) {
                  details.forEach((detail: any) => {
                    ctx.processor.addToSection(ORDER_TITLE, this.transformItem(item, detail));
                  });
                } else {
                  ctx.processor.addToSection(ORDER_TITLE, this.transformItem(item, {}));
                }
              });
            } catch (e) {
              this.handlerMessage.error({
                message: (e as Error)?.message,
                alternativeMessage: 'Error al preparar los datos de la exportación de entregas',
              });
            }
            ctx.next();
          },
          error: (err) => {
            this.handlerMessage.error({
              message: err?.error?.message,
              alternativeMessage: 'Error al obtener las entregas para exportar',
              statusCode: err?.status,
            });
            ctx.next();
          },
        });
      },
    }).start();
  }

  canStartTask() {
    if (this.exportService.taskIsActive(ORDER_EXPORT_ID)) {
      this.exportService.setVisible(true);
      return false;
    }
    return true;
  }
}
